/**
 * Core XRPL ecosystem organisations shown under the regional map.
 */

export type FlagshipEcosystemId =
  | "xrp-official"
  | "xrpl-foundation"
  | "xrpl-operations"
  | "xrpl-org"
  | "xrpl-grants";

export type FlagshipEcosystemOrg = {
  id: FlagshipEcosystemId;
  name: string;
  shortName: string;
  /** Full logo for hover / side panel. */
  logoSrc: string;
  /** Brand colour behind the X mark in the vertical panel. */
  iconBg: string;
  /** Use a light X mark on dark backgrounds. */
  iconMarkOnDark?: boolean;
  summary: string;
  focuses: readonly string[];
  href?: string;
  xHref?: string;
};

/** Same ledger mark as the early-bird side tab. */
export const FLAGSHIP_ECOSYSTEM_X_MARK = "/images/XRP%20Ledger%20-%20Symbol%20-%20Black.svg";

export const FLAGSHIP_XRP_ECOSYSTEM: readonly FlagshipEcosystemOrg[] = [
  {
    id: "xrpl-operations",
    name: "XRP Ledger Operations",
    shortName: "XRPL Operations",
    logoSrc: "/xrpl_regions/xrpl_ops.svg",
    iconBg: "#ffffff",
    summary:
      "The channel for XRP Ledger infrastructure and software updates — where validators, node operators, and builders follow protocol releases.",
    focuses: [
      "Protocol and software release announcements",
      "Amendment activations and upgrade guidance",
      "Validator and node operator notices",
      "Infrastructure and stability updates",
    ],
    xHref: "https://x.com/XRPLOperations",
  },
  {
    id: "xrpl-foundation",
    name: "XRP Ledger Foundation",
    shortName: "XRPL Foundation",
    logoSrc: "/xrpl_regions/xrp_f.svg",
    iconBg: "#000000",
    iconMarkOnDark: true,
    summary:
      "A non-profit that contributes to, grows, and advocates for the XRP Ledger and its community — keeping the network open, transparent, and accessible.",
    focuses: [
      "Protocol health and network infrastructure",
      "Core development and amendment evaluation",
      "Unique Node List (UNL) transparency and governance",
      "Open committees and community stewardship",
      "Long-term ecosystem sustainability",
    ],
    href: "https://xrpl.foundation/",
    xHref: "https://x.com/XRPLF",
  },
  {
    id: "xrp-official",
    name: "XRP",
    shortName: "XRP",
    logoSrc: "/xrpl_regions/xrp_offical.svg",
    iconBg: "#21e46b",
    summary:
      "The digital asset built for fast, low-cost global payments on the XRP Ledger — settling in seconds for a fraction of a penny.",
    focuses: [
      "Bridge asset for cross-border payments",
      "Native to the XRP Ledger since 2012",
      "Used by people, businesses, and institutions worldwide",
      "Open network with no single owner or issuer of new XRP",
    ],
    href: "https://www.xrp.org/",
    xHref: "https://x.com/XRP",
  },
  {
    id: "xrpl-org",
    name: "XRPL.org",
    shortName: "XRPL.org",
    logoSrc: "/xrpl_regions/xrpl_org.svg",
    iconBg: "#8a919a",
    summary:
      "The official home of XRP Ledger developer documentation — learn, build, and ship on the XRPL.",
    focuses: [
      "Official XRPL developer documentation",
      "API references and integration guides",
      "Tutorials for payments, tokenization, and DeFi",
      "Learning paths for new and advanced builders",
    ],
    href: "https://xrpl.org/",
  },
  {
    id: "xrpl-grants",
    name: "XRPL Grants",
    shortName: "XRPL Grants",
    logoSrc: "/xrpl_regions/xrp_grants.svg",
    iconBg: "#c0a7ff",
    summary:
      "Funding and accelerator programmes for teams building inclusive standards and products on the XRP Ledger.",
    focuses: [
      "Grants for projects building on the XRPL",
      "Accelerator programmes and mentorship",
      "Priority areas including DeFi, RWA, and payments",
      "Regional and thematic funds for builders worldwide",
    ],
    href: "https://xrplgrants.org/",
  },
];

export function getFlagshipEcosystemOrg(id: FlagshipEcosystemId): FlagshipEcosystemOrg {
  return FLAGSHIP_XRP_ECOSYSTEM.find((org) => org.id === id) ?? FLAGSHIP_XRP_ECOSYSTEM[0];
}
